import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';
import { useEffect, useRef, useState } from 'react';
import type { MetricKey } from '@/lib/analytics/metrics';

export interface DrilldownRow {
  row_id: string;
  row_ts: string; // ISO string
  payload: Record<string, unknown>;
}

interface UseMetricDrilldownArgs {
  workspaceId: string;
  metric: MetricKey | null; // null = sheet closed, no fetch
  from: Date;
  to: Date;
  userFilter: string | null;
  tz: string;
  segment: string | null;
  enabled?: boolean;
}

const PAGE_SIZE = 100; // RPC LIMIT 101 = page size + 1 for has-more detection

export function useMetricDrilldown(opts: UseMetricDrilldownArgs): {
  rows: DrilldownRow[];
  isLoading: boolean;
  error: Error | null;
  hasMore: boolean;
  scrollRef: React.RefObject<HTMLDivElement>;
} {
  const [cursor, setCursor] = useState<{ ts: string; id: string } | null>(null);
  const [accumulated, setAccumulated] = useState<DrilldownRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fromIso = opts.from.toISOString();
  const toIso = opts.to.toISOString();

  // Reset when key inputs change
  useEffect(() => {
    setCursor(null);
    setAccumulated([]);
    setHasMore(false);
  }, [opts.workspaceId, opts.metric, fromIso, toIso, opts.userFilter, opts.segment]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      ...queryKeys.analytics.drilldown(
        opts.workspaceId,
        opts.metric ?? '',
        fromIso,
        toIso,
        opts.userFilter,
        opts.segment,
      ),
      cursor?.ts ?? '',
      cursor?.id ?? '',
    ],
    queryFn: async () => {
      if (!opts.metric) return [];
      const { data, error } = await supabase.rpc('get_metric_drilldown', {
        p_workspace_id: opts.workspaceId,
        p_metric: opts.metric,
        p_from: fromIso,
        p_to: toIso,
        p_user_filter: opts.userFilter,
        p_tz: opts.tz,
        p_segment: opts.segment,
        p_cursor_ts: cursor?.ts ?? null,
        p_cursor_id: cursor?.id ?? null,
        p_limit: PAGE_SIZE + 1,
      });
      if (error) throw error;
      return (data ?? []) as DrilldownRow[];
    },
    enabled: !!opts.metric && (opts.enabled ?? true),
    staleTime: 60_000,
  });

  // Merge new page when data arrives
  useEffect(() => {
    if (!data) return;
    const more = data.length > PAGE_SIZE;
    setHasMore(more);
    const page = data.slice(0, PAGE_SIZE);
    if (cursor === null) {
      setAccumulated(page);
    } else {
      setAccumulated((prev) => {
        // Avoid duplicating if effect runs twice
        const seen = new Set(prev.map((r) => r.row_id));
        return [...prev, ...page.filter((r) => !seen.has(r.row_id))];
      });
    }
  }, [data, cursor]);

  // Scroll listener: when within 500px of bottom, advance cursor
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = () => {
      if (!hasMore || isLoading) return;
      const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
      if (distanceFromBottom < 500) {
        const last = accumulated[accumulated.length - 1];
        if (last) setCursor({ ts: last.row_ts, id: last.row_id });
      }
    };
    el.addEventListener('scroll', handler);
    return () => el.removeEventListener('scroll', handler);
  }, [hasMore, isLoading, accumulated]);

  return {
    rows: accumulated,
    isLoading,
    error: (error as Error) ?? null,
    hasMore,
    scrollRef,
  };
}
